import { postConciliation } from "modules/finops/conciliaciones/services/conciliacionService";
import { useState, useEffect } from "react";

export const useTablaFundadores = () => {
  // ==================== UTILS ====================
  const safeNumber = (num) => {
    const n = parseFloat(num);
    return isNaN(n) ? 0 : n;
  };

  const formatCurrency = (num) => `$${Math.round(safeNumber(num)).toLocaleString("es-CO")}`;

  // ==================== ESTADOS ====================
  const [conciliaciones, setConciliaciones] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedConciliacion, setSelectedConciliacion] = useState("");
  const [selectedEstado, setSelectedEstado] = useState("");
  const [datosCompletos, setDatosCompletos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);

  // ==================== EFECTOS ====================
  useEffect(() => {
    cargarDatosFiltros();
  }, []);

  // ==================== FUNCIONES DE CARGA ====================
  const cargarDatosFiltros = async () => {
    try {
      setLoading(true);
      const response = await postConciliation("fundadores");

      if (response?.data) {
        setDatosCompletos(response.data);
        setDatosFiltrados(response.data);

        // Filtros conciliaciones
        const llavesUnicas = [...new Set(response.data.map((i) => i.llave_conciliacion))];
        setConciliaciones(llavesUnicas.map((llave, idx) => ({ id: idx + 1, nombre: llave })));

        // Filtros estados
        const novedadesUnicas = [...new Set(response.data.map((i) => i.novedades_llave))];
        setEstados(novedadesUnicas.map((n, idx) => ({ id: idx + 1, nombre: n })));
      }
    } catch (error) {
      console.error("Error al cargar datos de filtros:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== PROCESAMIENTO ====================
  const procesarDatosConciliaciones = (data) => {
    const map = {};

    data.forEach((item) => {
      const llave = item.llave_conciliacion;

      if (!map[llave]) {
        map[llave] = {
          llave_conciliacion: llave,
          cantidadTX: 0,
          valorTransacciones: 0,
          comision: 0,
          ivaComision: 0,
          retefuente: 0,
          reteica: 0,
          aPagar: 0,
          transacciones: [],
          tieneNovedad: false,
        };
      }

      // Acumulados
      map[llave].cantidadTX++;
      map[llave].valorTransacciones += safeNumber(item.valor_servicio);
      map[llave].comision += safeNumber(item.comision);
      map[llave].ivaComision += safeNumber(item.iva_comision);
      map[llave].retefuente += safeNumber(item.retefuente);
      map[llave].reteica += safeNumber(item.reteica);

      // Calcular a pagar
      map[llave].aPagar =
        map[llave].valorTransacciones -
        map[llave].comision -
        map[llave].ivaComision -
        map[llave].retefuente -
        map[llave].reteica;

      map[llave].transacciones.push(item);

      if (item.novedades_llave && item.novedades_llave.trim().toUpperCase() !== "SIN NOVEDAD") {
        map[llave].tieneNovedad = true;
      }
    });

    return Object.values(map).map((c, idx) => ({
      id: idx + 1,
      conciliacion: {
        name: c.llave_conciliacion,
        referencia: `TX-${c.cantidadTX}`,
      },
      estadoConciliacion: c.tieneNovedad ? "Con novedad" : "Sin novedad",
      cantidadTX: c.cantidadTX,
      valorTransacciones: Math.round(c.valorTransacciones),
      comision: Math.round(c.comision),
      ivaComision: Math.round(c.ivaComision),
      retefuente: Math.round(c.retefuente),
      reteica: Math.round(c.reteica),
      aPagar: Math.round(c.aPagar),
    }));
  };

  const procesarConciliacionesConNovedad = (data) => {
    const map = {};

    data.forEach((item) => {
      if (
        item.estado_conciliacion === "CON NOVEDAD" &&
        item.novedades_llave === "Parking no procesado" &&
        item.estado_conciliacion_disponible !== "Conciliación No Disponible"
      ) {
        const llave = item.llave_conciliacion;

        if (!map[llave]) {
          map[llave] = { llave_conciliacion: llave, sinCUFE: 0, total: 0 };
        }

        map[llave].total++;
        map[llave].sinCUFE++;
      }
    });

    const resultados = Object.values(map);

    if (resultados.length > 0) {
      resultados.push({
        llave_conciliacion: "Total",
        sinCUFE: resultados.reduce((acc, curr) => acc + curr.sinCUFE, 0),
        total: resultados.reduce((acc, curr) => acc + curr.total, 0),
      });
    }

    return resultados;
  };

  const procesarCobroComision = (data) => {
    const map = {};

    data.forEach((item) => {
      const llave = item.llave_corte_comision;
      if (!llave) return;

      if (!map[llave]) {
        map[llave] = {
          llave_corte_comision: llave,
          cantidadTX: 0,
          valor_facturacion: 0,
          ivaValorFacturacion: 0,
        };
      }

      map[llave].valor_facturacion += safeNumber(item.valor_facturacion);
      map[llave].cantidadTX++;
      map[llave].ivaValorFacturacion += safeNumber(item.iva_valor_facturacion);
    });

    return Object.values(map);
  };

  const calcularEstadisticas = (conciliaciones) => {
    let totalPagar = 0,
      totalTX = 0,
      totalValorTransacciones = 0,
      conNovedad = 0,
      sinNovedad = 0;

    conciliaciones.forEach((c) => {
      totalPagar += c.aPagar || 0;
      totalTX += c.cantidadTX;
      totalValorTransacciones += c.valorTransacciones || 0;
      c.estadoConciliacion === "Con novedad" ? conNovedad++ : sinNovedad++;
    });

    return {
      totalPagarComercio: Math.round(totalPagar),
      totalTransacciones: totalTX,
      totalValorTransacciones: Math.round(totalValorTransacciones),
      conciliacionesConNovedad: conNovedad,
      conciliacionesSinNovedad: sinNovedad,
    };
  };

  // ==================== TABLAS ====================
  const datosProcesados = procesarDatosConciliaciones(datosFiltrados);
  const stats = calcularEstadisticas(datosProcesados);
  const datosComision = procesarCobroComision(datosCompletos);
  const datosNovedad = procesarConciliacionesConNovedad(datosFiltrados);

  const rowsComision = datosComision.map((i) => ({
    llave_corte_comision: i.llave_corte_comision,
    cantidadTX: i.cantidadTX,
    valorServicio: formatCurrency(i.valor_facturacion),
    ivaValorFacturacion: formatCurrency(i.ivaValorFacturacion),
  }));

  const rowsDetalle = datosFiltrados.map((i) => {
    const horaSalida = i.hora_salida
      ? i.hora_salida
      : i.fecha_salida
      ? new Date(i.fecha_salida).toLocaleTimeString("es-CO", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : "-";

    return {
      fechaIngreso: i.fecha_ingreso || "-",
      horaIngreso: i.hora_ingreso || "-",
      fechaSalida: i.fecha_salida ? new Date(i.fecha_salida).toLocaleDateString("es-CO") : "-",
      horaSalida,
      tiempo: i.tiempo_parqueo || "-",
      placa: i.placa || "-",
      idtransaction: i.idtransaction || "-",
      subtotal: formatCurrency(i.subtotal),
      valorDescuento: formatCurrency(i.valor_descuento),
      valorImpuesto: formatCurrency(i.valor_impuesto),
      valorServicio: formatCurrency(i.valor_servicio),
      valorPagado: formatCurrency(i.valor_pagado),
      factura: i.factura || "-",
      cufe: i.cufe_factura || "-",
      fechaTransaccion: i.fecha_transaccion || "-",
    };
  });

  const rowsNovedad = datosNovedad.map((i) => ({
    tipoConciliacion: i.llave_conciliacion,
    sinCUFE: i.sinCUFE,
    total: i.total,
  }));

  // ==================== HANDLERS ====================
  const handleFiltrar = () => {
    let filtrados = [...datosCompletos];
    if (selectedConciliacion)
      filtrados = filtrados.filter((i) => i.llave_conciliacion === selectedConciliacion);
    if (selectedEstado) filtrados = filtrados.filter((i) => i.novedades_llave === selectedEstado);
    setDatosFiltrados(filtrados);
  };

  const handleLimpiarFiltros = () => {
    setSelectedConciliacion("");
    setSelectedEstado("");
    setDatosFiltrados(datosCompletos);
  };

  return {
    // Estados
    conciliaciones,
    estados,
    selectedConciliacion,
    setSelectedConciliacion,
    selectedEstado,
    setSelectedEstado,
    loading,
    rowsComision,
    rowsDetalle,
    rowsNovedad,
    datosProcesados,
    stats,
    datosNovedad,
    handleFiltrar,
    handleLimpiarFiltros,
  };
};
